import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Area, ZipRange } from '@tourfold/libraries/tf-api-client-public-v2';
import {
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  TfColumnDefDirective,
  TfDataTableComponent,
} from '@tourfold/libraries/shared-ui';
import {
  resolveSettingsLoadErrorDetails,
  SettingsCard,
  SettingsCardActions,
  SettingsCardNew,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import { readTabFromQueryParams, syncTabToUrl } from '@tourfold/libraries/shared-util';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { AreaMiniMap } from '../area-mini-map/area-mini-map.component';
import { AreaFormDialog } from '../area-form-dialog/area-form-dialog';
import { AreaFormResult } from '../area-form-dialog/area-form-dialog.types';
import { provideAreasAiPageContext } from '../../settings-areas.ai-page-context';
import { AreasService } from './areas.service';

type AreasView = 'cards' | 'table';
const AREAS_VIEWS: readonly AreasView[] = ['cards', 'table'];
const VIEW_QUERY_PARAM = 'view';

@Component({
  selector: 'tf-settings-areas-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ConfirmDialogModule,
    TooltipModule,
    TranslateModule,
    AreaMiniMap,
    AreaFormDialog,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsCard,
    SettingsCardActions,
    SettingsCardNew,
    SettingsInlineMessage,
    SettingsLoadError,
    TfDataTableComponent,
    TfColumnDefDirective,
  ],
  templateUrl: './areas-list.html',
  styleUrl: './areas-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class Areas {
  private readonly areasService = inject(AreasService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly searchSubject = new Subject<string>();

  readonly canCreateArea = computed(() => this.resourceAccessService.canPerform('areas', 'create'));
  readonly canUpdateArea = computed(() => this.resourceAccessService.canPerform('areas', 'update'));
  readonly canDeleteArea = computed(() => this.resourceAccessService.canPerform('areas', 'delete'));

  // Areas count is small in practice; load everything once and filter client-side.
  // Public for template branches (skeleton / empty / populated).
  readonly allAreas = signal<readonly Area[]>([]);

  readonly searchQuery = signal<string>('');
  // `isLoading` is the "no data, no idea how long the network will take" state — the
  // template only renders the skeleton when this is true. It's deliberately delayed by
  // `SKELETON_DELAY_MS` after the request starts so fast responses never flash a skeleton.
  // `isReloading` is the "we have data, fetching fresh in the background" state — we don't
  // render anything for it today, but it's available for a future quiet indicator.
  readonly isLoading = signal(false);
  readonly isReloading = signal(false);
  // Flips to `true` once we've hydrated from cache OR seen the first response (success or
  // failure). The template uses it to switch between the skeleton, the empty state, and
  // the grid; once true it stays true so re-fetches don't drop back to the skeleton.
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly isSaving = signal(false);
  readonly isDialogVisible = signal(false);
  readonly editingArea = signal<Area | null>(null);

  /**
   * Card-grid vs. data-table layout for the populated list. Synced to `?view=` on the URL
   * so refreshes / shared links preserve the user's choice. Defaults to `cards`.
   */
  readonly view = signal<AreasView>('cards');

  private skeletonTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly SKELETON_DELAY_MS = 250;

  private readonly dialog = viewChild(AreaFormDialog);

  readonly visibleAreas = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return this.allAreas();
    return this.allAreas().filter((a) => (a.name ?? '').toLowerCase().includes(query));
  });
  readonly showEmptyState = computed(
    () => this.hasLoadedOnce() && this.searchQuery().trim() === '' && this.allAreas().length === 0
  );

  /**
   * Mutable copy of the visible list for `tf-data-table`, which types its `value`
   * input as `T[]` (mutable). The data-table component never writes to the array, but its
   * type signature precludes binding a `readonly` array directly. Cheap shallow copy.
   */
  readonly visibleAreasForTable = computed(() => [...this.visibleAreas()]);

  readonly skeletonCount = [0, 1, 2];

  /**
   * Column definitions for the table view. The `_thumb`, `_name`, and `_actions` fields are
   * rendered via `tfColumnDef` custom templates so we can compose the polygon
   * thumbnail, swatch+name+description stack, and edit/delete buttons respectively.
   */
  readonly tableColumns: ColumnDef[] = [
    { field: '_thumb', title: '', type: 'custom', width: '92px' },
    { field: '_name', title: 'SETTINGS_AREAS.TABLE.NAME', type: 'custom', width: '32%' },
    { field: '_zipRanges', title: 'SETTINGS_AREAS.TABLE.ZIP_RANGES', type: 'custom', width: '28%' },
    {
      field: 'updatedAt',
      title: 'SETTINGS_AREAS.TABLE.UPDATED',
      type: 'date',
      dateFormat: 'mediumDate',
      width: '18%',
    },
    { field: '_actions', title: 'SETTINGS_AREAS.TABLE.ACTIONS', type: 'custom', width: '120px' },
  ];

  constructor() {
    this.setupSearchDebounce();
    provideAreasAiPageContext({
      visibleAreas: this.visibleAreas,
      searchQuery: this.searchQuery,
      editingArea: this.editingArea,
    });
    this.view.set(this.readViewFromRoute());
    // Hydrate from the service-level cache synchronously so re-visiting the page renders
    // the previous result immediately instead of flashing a skeleton. The background
    // refetch (loadAreas) still happens so the user sees any out-of-band changes.
    const cached = this.areasService.cachedAreas();
    if (cached) {
      this.allAreas.set(cached);
      this.hasLoadedOnce.set(true);
    }
    this.loadAreas();
  }

  reload(): void {
    this.loadAreas();
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  setView(view: AreasView): void {
    if (this.view() === view) return;
    this.view.set(view);
    syncTabToUrl(this.router, this.route, VIEW_QUERY_PARAM, view);
  }

  private readViewFromRoute(): AreasView {
    const snapshot = this.route.snapshot.queryParams as Record<string, string>;
    return readTabFromQueryParams(snapshot, VIEW_QUERY_PARAM, AREAS_VIEWS, 'cards') as AreasView;
  }

  createArea(): void {
    this.editingArea.set(null);
    this.isDialogVisible.set(true);
  }

  editArea(area: Area): void {
    this.editingArea.set(area);
    this.isDialogVisible.set(true);
  }

  onDialogVisibilityChange(visible: boolean): void {
    if (!visible) {
      this.isDialogVisible.set(false);
      this.editingArea.set(null);
    }
  }

  onDialogCancel(): void {
    this.isDialogVisible.set(false);
    this.editingArea.set(null);
  }

  async onDialogSave(result: AreaFormResult): Promise<void> {
    const editing = this.editingArea();
    this.isSaving.set(true);
    this.actionError.set(null);
    try {
      // No success toasts on create/update — the dialog closes and the new/updated card
      // is immediately visible in the grid (or row in the table), which is feedback
      // enough. Same rationale as the delete flow: a transient toast on every successful
      // mutation adds dismissal cost without telling the user anything they don't already
      // see. Errors still toast, because those *do* need attention.
      if (editing?.id) {
        await firstValueFrom(
          this.areasService.updateArea(editing.id, {
            name: result.name,
            description: result.description ?? undefined,
            color: result.color ?? undefined,
            zipRanges: result.zipRanges,
            geoJson: result.geoJson,
            lockVersion: editing.lockVersion,
          })
        );
      } else {
        await firstValueFrom(
          this.areasService.createArea({
            name: result.name,
            description: result.description ?? undefined,
            color: result.color ?? undefined,
            zipRanges: result.zipRanges,
            geoJson: result.geoJson,
          })
        );
      }
      this.isDialogVisible.set(false);
      this.editingArea.set(null);
      this.loadAreas();
    } catch (error: unknown) {
      const applied = await this.dialog()?.applyServerError(error);
      // If the dialog showed inline errors next to specific fields, the user already has
      // everything they need to fix the problem — surfacing the same content as a toast
      // adds noise without value. Only fall back to a toast when the failure is opaque
      // (no inline mapping, no general message) so we don't leave the user staring at a
      // failed save with nothing to act on.
      if (applied?.hasFieldErrors) {
        return;
      }
      const fallbackKey = editing
        ? 'SETTINGS_AREAS.ERROR.AREA_UPDATE_FAILED'
        : 'SETTINGS_AREAS.ERROR.AREA_CREATE_FAILED';
      const generalMessages = applied?.generalMessages ?? [];
      const detail = extractProblemDetail(error);
      const message =
        generalMessages.length > 0
          ? generalMessages.join(' ')
          : (detail ?? this.translateService.instant(fallbackKey));
      this.actionError.set(message);
    } finally {
      this.isSaving.set(false);
    }
  }

  deleteArea(area: Area): void {
    if (!area.id) return;
    const areaId = area.id;
    const message = this.translateService.instant('SETTINGS_AREAS.DELETE_CONFIRM_MESSAGE', {
      name: area.name ?? '',
    }) as string;

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_AREAS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.areasService.deleteArea(areaId).subscribe({
          next: () => {
            // No success toast: the card disappearing from the list is feedback enough,
            // and a transient toast on every delete adds noise that the user has to
            // dismiss before moving on. Errors still toast — the user does need to know
            // when a delete didn't take.
            this.isLoading.set(false);
            this.loadAreas();
          },
          error: () => {
            this.isLoading.set(false);
            this.actionError.set(
              this.translateService.instant('SETTINGS_AREAS.ERROR.AREA_DELETE_FAILED')
            );
          },
        });
      },
    });
  }

  /**
   * Maximum chips rendered inline in cards and table cells before the "+N more" indicator
   * shows up. Three keeps the row visually quiet for typical workspaces (1-5 ranges) while
   * still surfacing enough to be useful.
   */
  static readonly MAX_VISIBLE_ZIP_CHIPS = 3;

  visibleZipRanges(area: Area): readonly ZipRange[] {
    return (area.zipRanges ?? []).slice(0, Areas.MAX_VISIBLE_ZIP_CHIPS);
  }

  hiddenZipRangeCount(area: Area): number {
    const total = area.zipRanges?.length ?? 0;
    return Math.max(0, total - Areas.MAX_VISIBLE_ZIP_CHIPS);
  }

  /**
   * Formats a single zip range as a chip label: `AUT 1010` (single zip) or `AUT 1010–1019`
   * (range). Country is always shown because areas now support mixed-country zip ranges,
   * so dropping the country would let two different `1010`s read as duplicates on a card.
   */
  formatZipRange(range: ZipRange): string {
    const country = (range.country ?? '').trim();
    const from = (range.from ?? '').trim();
    const to = (range.to ?? '').trim();
    const zip = from && to && from !== to ? `${from}–${to}` : from || to;
    if (!zip) return country;
    return country ? `${country} ${zip}` : zip;
  }

  /**
   * Relative-ish timestamp for the card footer. Avoids a date-fns dependency: prints "today",
   * "yesterday", "N days ago" for the first week then falls back to a locale-formatted date.
   */
  relativeUpdated(area: Area): string {
    const updated = (area.updatedAt ?? area.createdAt) as Date | string | undefined;
    if (!updated) return '';
    const stamp = updated instanceof Date ? updated : new Date(updated);
    if (Number.isNaN(stamp.getTime())) return '';
    const diffMs = Date.now() - stamp.getTime();
    const day = 24 * 60 * 60 * 1000;
    if (diffMs < day) return this.translateService.instant('SETTINGS_AREAS.UPDATED.TODAY');
    if (diffMs < 2 * day) return this.translateService.instant('SETTINGS_AREAS.UPDATED.YESTERDAY');
    const days = Math.floor(diffMs / day);
    if (days < 7) {
      return this.translateService.instant('SETTINGS_AREAS.UPDATED.DAYS_AGO', { count: days });
    }
    return stamp.toLocaleDateString();
  }

  private setupSearchDebounce() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => this.searchQuery.set(query));
  }

  private loadAreas(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.actionError.set(null);
    // If we already have data (initial render or from a previous load) keep the grid on
    // screen and refresh silently. Only schedule the skeleton when we have nothing to
    // show, and even then delay it so a sub-250ms response never causes a flash.
    if (this.hasLoadedOnce()) {
      this.isReloading.set(true);
    } else {
      this.scheduleSkeleton();
    }
    this.areasService.listAreas().subscribe({
      next: (response) => {
        this.cancelSkeleton();
        this.allAreas.set(response.items ?? []);
        this.isLoading.set(false);
        this.isReloading.set(false);
        this.loadError.set(null);
        this.loadErrorDetails.set(null);
        this.initialLoadFailed.set(false);
        this.hasLoadedOnce.set(true);
      },
      error: (error: unknown) => {
        this.cancelSkeleton();
        this.isLoading.set(false);
        this.isReloading.set(false);
        const message = this.translateService.instant('SETTINGS_AREAS.ERROR.AREA_LOAD_FAILED');
        if (hadLoadedOnce) {
          this.hasLoadedOnce.set(true);
          this.actionError.set(message);
        } else {
          this.loadError.set(message);
          this.initialLoadFailed.set(true);
          void this.updateLoadErrorDetails(error);
        }
      },
    });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }

  private scheduleSkeleton(): void {
    this.cancelSkeleton();
    this.skeletonTimer = setTimeout(() => {
      this.isLoading.set(true);
      this.skeletonTimer = null;
    }, Areas.SKELETON_DELAY_MS);
  }

  private cancelSkeleton(): void {
    if (this.skeletonTimer !== null) {
      clearTimeout(this.skeletonTimer);
      this.skeletonTimer = null;
    }
  }
}

/**
 * Best-effort RFC 9457 ProblemDetail extraction for surfacing a useful toast when the
 * dialog's inline SignalFormServerErrors has nothing to map. The generated API client
 * wraps the response in a typed error, but the actual payload shape lives behind a few
 * common keys (`error.detail`, `error.title`, `error.message`). Returns `null` if none
 * of them produce a usable string so the caller can fall back to its localised default.
 */
function extractProblemDetail(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const candidate = (error as { error?: unknown }).error ?? error;
  if (!candidate || typeof candidate !== 'object') return null;
  const node = candidate as Record<string, unknown>;
  const detail = node['detail'];
  if (typeof detail === 'string' && detail.length > 0) return detail;
  const title = node['title'];
  if (typeof title === 'string' && title.length > 0) return title;
  const message = node['message'];
  if (typeof message === 'string' && message.length > 0) return message;
  return null;
}
